import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Flag, ExternalLink } from "lucide-react";

const STATUS_BADGE = {
  open: "bg-amber-100 text-amber-700", resolved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-slate-300 text-slate-700", need_info: "bg-blue-100 text-blue-700",
};

export default function AdminCorrections() {
  const [data, setData] = useState({ items: [], counts: {} });
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/live/admin/correction-requests", { params: status ? { status } : {} })
      .then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [status]);

  const resolve = async (id, newStatus) => {
    const resolution = newStatus === "resolved" ? window.prompt("Resolution note (optional):") : window.prompt(`Reason for ${newStatus}:`);
    if (newStatus !== "resolved" && !resolution) return;
    try {
      await api.post(`/live/admin/correction-requests/${id}/resolve`, { status: newStatus, resolution });
      toast.success(`Marked ${newStatus}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto p-6" data-testid="admin-corrections">
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><Flag className="w-4 h-4" /> Data Integrity</div>
        <h1 className="font-display text-3xl uppercase tracking-tight">Correction Requests</h1>
        <p className="text-sm text-slate-500 mt-1">Public-submitted stat disputes. Official data is never changed automatically.</p>

        <div className="flex flex-wrap gap-2 mt-4 mb-4">
          {["open", "need_info", "resolved", "rejected", ""].map((s) => (
            <button key={s || "all"} data-testid={`corr-filter-${s || "all"}`} onClick={() => setStatus(s)} className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${status === s ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white border-slate-300 text-slate-600"}`}>
              {s || "All"}{data.counts[s] != null ? ` (${data.counts[s]})` : ""}
            </button>
          ))}
        </div>

        {loading ? <div className="label-overline text-slate-500 py-8">Loading…</div>
          : data.items.length === 0 ? <div className="border border-dashed border-slate-300 rounded-sm p-10 text-center text-sm text-slate-500">No correction requests.</div>
            : (
              <div className="space-y-3">
                {data.items.map((r) => (
                  <div key={r.id} data-testid={`corr-${r.id}`} className="bg-white border border-slate-200 rounded-sm p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${STATUS_BADGE[r.status]}`}>{r.status}</span>
                          <span className="text-xs text-slate-400 uppercase">{r.field}</span>
                        </div>
                        <div className="text-sm mt-1">
                          {r.game && <span className="text-slate-500">{r.game.date} vs {r.game.opponent} · </span>}
                          {r.player && <Link to={`/prospects/${r.player.slug}`} className="text-[#0A192F] font-semibold hover:text-[#E63946]">{r.player.first_name} {r.player.last_name}</Link>}
                        </div>
                        <div className="text-xs text-slate-600 mt-1">
                          {(r.current_value || r.suggested_value) && <span className="mr-3">"{r.current_value || "—"}" → "{r.suggested_value || "—"}"</span>}
                        </div>
                        <p className="text-sm text-slate-700 mt-1 italic">"{r.reason}"</p>
                        {r.evidence_url && <a href={r.evidence_url} target="_blank" rel="noreferrer" className="text-xs text-[#E63946] inline-flex items-center gap-1 mt-1">Evidence <ExternalLink className="w-3 h-3" /></a>}
                        {r.reporter_name && <div className="text-[11px] text-slate-400 mt-1">Reported by {r.reporter_name} {r.reporter_email ? `· ${r.reporter_email}` : ""}</div>}
                        {r.resolution && <div className="text-xs text-slate-500 mt-1 bg-slate-50 rounded-sm p-2">Resolution: {r.resolution}</div>}
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        {r.game && <Link to={`/admin/games/approval/${r.game_id}`} className="text-xs text-blue-600 underline">Review game</Link>}
                        {r.status === "open" && (
                          <>
                            <button data-testid={`corr-resolve-${r.id}`} onClick={() => resolve(r.id, "resolved")} className="text-xs bg-emerald-600 text-white px-2 py-1 rounded-sm">Resolve</button>
                            <button onClick={() => resolve(r.id, "need_info")} className="text-xs border border-blue-300 text-blue-600 px-2 py-1 rounded-sm">Need Info</button>
                            <button onClick={() => resolve(r.id, "rejected")} className="text-xs border border-slate-300 text-slate-500 px-2 py-1 rounded-sm">Reject</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
      </div>
    </AdminLayout>
  );
}
